import React, { createContext, useContext, useState, useCallback } from 'react';
import recipeService from '../services/recipeService';
import { normalizeRecipeListFromApi } from '../utils/normalizeRecipe';

const RecipeContext = createContext();

export const useRecipe = () => {
  const context = useContext(RecipeContext);
  if (!context) {
    throw new Error('useRecipe must be used within a RecipeProvider');
  }
  return context;
};

export const RecipeProvider = ({ children }) => {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);

  const fetchRecipes = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const recipeData = await recipeService.getRecipes(filters);
      
      // Handle different response formats
      const recipesArray = Array.isArray(recipeData) ? recipeData : 
                          recipeData.recipeResult || recipeData.recipes || [];

      if (recipeData.pagination) {
        setPagination(recipeData.pagination);
      } else {
        setPagination({
          total: recipesArray.length,
          page: 1,
          limit: recipesArray.length || 20,
          pages: 1
        });
      }

      const processedRecipes = normalizeRecipeListFromApi(recipeData);

      setRecipes(processedRecipes);
      return processedRecipes;
    } catch (error) {
      console.error('Error fetching recipes:', error);
      setError('Error al cargar las recetas');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshRecipes = useCallback(async (filters = {}) => {
    return await fetchRecipes(filters);
  }, [fetchRecipes]);

  const updateRecipe = useCallback(async (recipeId, recipeData) => {
    try {
      const result = await recipeService.updateRecipe(recipeId, recipeData);
      
      // Update the local state with the updated recipe
      setRecipes(prevRecipes => 
        prevRecipes.map(recipe => 
          recipe.id === recipeId ? { ...recipe, ...result.recipe } : recipe
        )
      );
      
      return result;
    } catch (error) {
      console.error('Error updating recipe:', error);
      throw error;
    }
  }, []);

  const value = {
    recipes,
    loading,
    error,
    pagination,
    fetchRecipes,
    refreshRecipes,
    updateRecipe,
    setRecipes
  };

  return (
    <RecipeContext.Provider value={value}>
      {children}
    </RecipeContext.Provider>
  );
};

export default RecipeContext;
